import { defineComponent as _defineComponent } from 'vue'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const __default__ = require('@nuxtjs/composition-api').defineComponent({
  name: 'TeachingResourceCollection',
  layout: 'default',
  head: {},
  jsonld() {
    // @ts-ignore
    return this.$jsonSchemaPage(this.title, this.$route.path);
  },
});

import {
  computed,
  ref,
  useAsync,
  useContext,
  useMeta,
  useRoute,
  useRouter,
  useStore,
  watch,
} from '@nuxtjs/composition-api';
import TBreadcrumbs from '~/components/atoms/t-breadcrumbs.vue';
import ExpandableDescription from '~/components/category/ExpandableDescription.vue';
import ResourcesList from '~/components/resource/ResourcesList.vue';
import CountrySwitcher from '~/components/CountrySwitcher.vue';
import FilteredPageLinks from '~/components/category/FilteredPageLinks.vue';
import { longCategoryTitle, breadcrumbs } from '~/utils/category';
import { useUser } from '~/composables/useUser';
import { useFilteredLandingPage } from '~/composables/useFilteredLandingPage';
import { State } from '~/store';
import { Category } from '~/types/category';
import { RootCategory, PublicV1FiltersRequest } from '~/types/api';
import { useTrackPageView } from '~/composables/useTrackPageView';
import { useLocale } from '~/composables/useLocale';


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose }) {

const $router = useRouter();
const $route = useRoute();
const {
  i18n,
  $mzAnalyticsApi,
  $constructEventParamsCategory,
  error,
  $metaPropertiesCategory,
  $hrefLangsResource,
  $handleRedirect,
} = useContext();
const { slug, yearLevels, resourceTypes, getFilteredLandingPagePillValues } =
  useFilteredLandingPage($route.value.params);
const store = useStore<State>();

const locale = useLocale();
const root = computed<RootCategory>(() => `resource-collection-${locale.value}`);

const resourceParams = computed<PublicV1FiltersRequest>(() => ({
  country: locale.value,
  category: slug,
  perPage: 24,
  root: root.value,
  resource_types: resourceTypes,
  year_levels: yearLevels,
}));
const availableFilters = [
  'types',
  'learning_areas',
  'year_levels',
  'resource_types',
  'availability',
  'file_formats',
  'publishers',
];
const parent = computed(() => ({
  link: '/' + i18n.locale + '/teaching-resource-collections/',
  name: 'Teaching Resource Collections',
}));
const { user } = useUser();

const normalisedBreadCrumbs = computed(() =>
  breadcrumbs(category.value?.breadcrumbs, parent.value)
);

const title = computed(() =>
  category.value
    ? longCategoryTitle(
        category.value.name,
        locale.value,
        resourceTypes[0] || undefined,
        yearLevels[0] || undefined
      )
    : null
);

const isLoading = ref(false);

// TODO: This is almost identical to learning-area/_.vue.  That should also be updated to use the composition
// API and then this can be refactored to use a shared composable.
const category = useAsync(async () => {
  if (process.browser) {
    isLoading.value = true;
  }
  try {
    // TODO: Remove this store call. There is no need to use the store here as the category isn't reused,
    // we can just use the API directly. Removing will also help move away from vuex
    const _category: Category = await store.dispatch('loadCategory', {
      country: locale.value,
      category: slug,
      root: root.value,
      link: $route.value.path,
    });

    return {
      ..._category,
      filters: await getFilteredLandingPagePillValues(resourceParams.value),
    };
  } catch (err) {
    try {
      await $handleRedirect($route.value.path);
    } catch (err) {
      // No page matching this route
      error({ statusCode: 404, message: err instanceof Error ? err.message : String(err) });
    }
  } finally {
    if (process.browser) {
      isLoading.value = false;
    }
  }
  return null;
}, `category-${slug}-${locale.value}`);

const stripResourceTypeOutOfUrl = (url: string): void => {
  const regexGetGroups = /teaching-resource-collection\/(.*)\/(.*)\/(.*)/;
  if (regexGetGroups.exec(url)?.[2] === undefined) {
    return;
  }

  $router.push(url.replace(regexGetGroups, 'teaching-resource-collection/$1/$3'));
};
watch($route, to => {
  if (!to.query.page && Object.keys(to.query).length !== 0) {
    stripResourceTypeOutOfUrl(to.fullPath);
  }
});

useTrackPageView(category, newCategory => {
  $mzAnalyticsApi({
    event: 'pageView',
  });
});

useMeta(() => ({
  title: title.value || undefined,
  meta: category.value
    ? $metaPropertiesCategory(category.value, { title: title.value || '' })
    : undefined,
  link: category.value
    ? $hrefLangsResource(
        category.value?.linked,
        category.value?.link,
        i18n.locale,
        $route.value,
        resourceTypes[0] || yearLevels[0]
      )
    : undefined,
}));
expose({
  title,
});

return { __sfc: true,$router, $route, i18n, $mzAnalyticsApi, $constructEventParamsCategory, error, $metaPropertiesCategory, $hrefLangsResource, $handleRedirect, slug, yearLevels, resourceTypes, getFilteredLandingPagePillValues, store, locale, root, resourceParams, availableFilters, parent, user, normalisedBreadCrumbs, title, isLoading, category, stripResourceTypeOutOfUrl, TBreadcrumbs, ExpandableDescription, ResourcesList, CountrySwitcher, FilteredPageLinks }
}

})